import type {ReactNode} from "react";
import type {OtsBadgeTone} from "../../components/OtsBadge";

/** Something a map panel names and could link to: a site, company, route, trailer... */
export type MapEntityRef = {
  id?: string;
  name?: string;
  /** The app's object type, e.g. "SITE", "COMPANY", "ROUTE", "CONTAINER". */
  type: string;
};

/**
 * Draws an entity's name. The apps pass one that links to its profile; ots-ui-tw cannot know their routes, so
 * by default it is plain text.
 */
export type RenderMapEntity = (ref: MapEntityRef) => ReactNode;

export const plainMapEntity: RenderMapEntity = ({name, id}) => name ?? id ?? "—";

export const formatMapNumber = (value: number | undefined, maximumFractionDigits = 1) =>
  value == null ? undefined : value.toLocaleString(undefined, {maximumFractionDigits});

/** "130 bbl", or just the number when the units are unknown. */
export const formatMapQuantity = (value: number | undefined, units?: string) => {
  const number = formatMapNumber(value);
  if (number == null) return undefined;
  return units ? `${number} ${units.toLowerCase()}` : number;
};

/** Five decimals: about a meter, enough to find a truck on a pad. */
export const formatCoordinates = (latitude: number, longitude: number) =>
  `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;

/** Operational service status codes (ops mode) to badge tones. */
export const opsStatusTone = (status: string | undefined): OtsBadgeTone => {
  switch (status) {
    case "CLEAR": return "success";
    case "TREATED": return "violet";
    case "IN_PROGRESS": return "blue";
    case "NEEDS_SERVICE": return "warning";
    case "BLOCKED": return "danger";
    case "OPEN": return "info";
    default: return "neutral";
  }
};
